import type {
  AuditAction,
  AuditActor,
  AuditChanges,
  AuditEntity,
  AuditEvent,
} from '@sirius/contracts/admin'
import {
  AUDIT_ACTIONS,
  AUDIT_ENTITIES,
  TENANT_VISIBLE_AUDIT_ENTITIES,
} from '@sirius/contracts/admin'
import { endOfDay, startOfDay } from 'date-fns'
import type { TFunction } from 'i18next'
import type { DateRange } from 'react-day-picker'
import { formatCredits, formatDateTime } from '~/i18n/format'
import {
  LANGUAGE_NAMES,
  isCabinetLanguage,
} from '~/pages/cabinets/cabinetLanguage'
import type { GameNames } from '~/pages/cabinets/gameNames'
import { formatRtp } from '~/pages/games/rtp'
import type { AdminRole } from '~/providers/authProvider'

/**
 * Whether tenant users meet an action in their log. Only the events of
 * `TENANT_VISIBLE_AUDIT_ENTITIES` reach them, so the actions of users and
 * sessions are staff-only filter options (a role change is a member's too),
 * and of the games' only those of the tenant's own configurations (a new
 * one, a publish, a discarded draft).
 */
const TENANT_ACTIONS: Record<AuditAction, boolean> = {
  create: true,
  update: true,
  delete: true,
  set_status: true,
  issue_key: true,
  sign_in: false,
  sign_in_failed: false,
  sign_out: false,
  ban: false,
  unban: false,
  role_change: true,
  password_change: false,
  password_reset: false,
  email_change: false,
  member_add: true,
  member_remove: true,
  invitation_create: true,
  invitation_accept: true,
  invitation_revoke: true,
  configuration_create: true,
  configuration_publish: true,
  configuration_discard: true,
  set_default_configuration: false,
  config_version_create: false,
  set_default_config: false,
  // a save of a tenant's own configuration from before drafts
  configuration_update: true,
  // a cabinet's credits, and its key used by two pages at once
  credit_in: true,
  credit_out: true,
  second_page_detected: true,
}

export function actionLabel(action: AuditAction, t: TFunction) {
  return t(`activityLog.action.${action}`)
}

export function entityTypeLabel(entity: AuditEntity, t: TFunction) {
  return t(`activityLog.entity.${entity}`)
}

/**
 * Options of the action filter (combobox): platform admins get the auth
 * actions too.
 */
export function actionOptions(role: AdminRole, t: TFunction) {
  return AUDIT_ACTIONS.filter(
    (action) => role === 'admin' || TENANT_ACTIONS[action],
  ).map((value) => ({ value, label: actionLabel(value, t) }))
}

/**
 * Options of the entity filter (combobox): platform admins get every entity,
 * a technician the cabinets (the API gives it cabinet events only).
 */
export function entityOptions(role: AdminRole, t: TFunction) {
  const visible: readonly AuditEntity[] =
    role === 'technician' ? ['cabinet'] : TENANT_VISIBLE_AUDIT_ENTITIES
  return AUDIT_ENTITIES.filter(
    (entity) => role === 'admin' || visible.includes(entity),
  ).map((value) => ({ value, label: entityTypeLabel(value, t) }))
}

/** The tenant an event belongs to; "Platform" for sign-ins, users, games and the like. */
export function tenantLabel(tenant: AuditEvent['tenant'], t: TFunction) {
  return tenant?.name ?? t('activityLog.tenant.platform')
}

/** The record's name as the log knows it (`entityLabel`). */
export function entityName(label: string | null, t: TFunction) {
  return label ?? t('activityLog.unnamed')
}

/** Who acted. Staff stay anonymous for tenant users (the API sends `staff`). */
export function actorLabel(actor: AuditActor, t: TFunction) {
  switch (actor.kind) {
    case 'user':
      return actor.name
    case 'staff':
      return t('activityLog.actor.staff')
    case 'deleted':
      return t('activityLog.actor.deletedUser')
    case 'anonymous':
      // nobody was signed in (a failed sign-in)
      return t('activityLog.actor.anonymous')
  }
}

/**
 * The catalog's label of the field (`activityLog.field.<field>`); a field it
 * has no label for gets a humanised one: `locationId` → "Location ID",
 * `keyIssuedAt` → "Key issued at" (English only).
 */
export function fieldLabel(field: string, t: TFunction) {
  return t(`activityLog.field.${field}`, { defaultValue: humanise(field) })
}

function humanise(field: string) {
  const words = field
    .split(/(?=[A-Z])/)
    .map((word) => (word === 'Id' ? 'ID' : word.toLowerCase()))
  const sentence = words.join(' ')
  return sentence.charAt(0).toUpperCase() + sentence.slice(1)
}

const ISO_TIMESTAMP =
  /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?(?:Z|[+-]\d{2}:?\d{2})$/

/** One side of a change: dash for nothing, local time for timestamps, JSON for objects. */
export function formatChangeValue(value: unknown): string {
  if (value === null || value === undefined) {
    return '—'
  }
  if (typeof value === 'string') {
    return ISO_TIMESTAMP.test(value) ? formatDateTime(new Date(value)) : value
  }
  if (
    typeof value === 'number' ||
    typeof value === 'boolean' ||
    typeof value === 'bigint'
  ) {
    return String(value)
  }
  return JSON.stringify(value)
}

/**
 * One side of a cabinet's `gameIds` change: the games by name, sorted.
 * "None" for no games, a dash for nothing (a create's `from`), "…" while the
 * names load. A game without a name (removed since, or the names failed to
 * load) keeps its id: the log never hides a game.
 */
export function formatGameIds(
  value: unknown,
  names: GameNames,
  t: TFunction,
): string {
  if (!Array.isArray(value)) {
    return formatChangeValue(value)
  }
  if (value.length === 0) {
    return t('activityLog.value.none')
  }
  if (names.isLoading) {
    return '…'
  }
  return value
    .map((id) => names.byId.get(String(id)) ?? String(id))
    .toSorted((left, right) => left.localeCompare(right))
    .join(', ')
}

/**
 * One side of a cabinet's `configurations` or a tenant's
 * `defaultConfigurations` change (staff and the tenant's owners/admins read
 * it in the log): each game's configuration by game name (`Book of Ra 2: Low
 * volatility`), sorted. "Default" for none (every game on Use default), a
 * dash for nothing (a create's `from`), "…" while the names load; a game
 * without a name keeps its id.
 */
export function formatConfigurations(
  value: unknown,
  names: GameNames,
  t: TFunction,
): string {
  if (!Array.isArray(value)) {
    return formatChangeValue(value)
  }
  if (value.length === 0) {
    return t('activityLog.value.default')
  }
  if (names.isLoading) {
    return '…'
  }
  return value
    .map((entry: unknown) => {
      if (
        typeof entry !== 'object' ||
        entry === null ||
        !('gameId' in entry) ||
        !('configuration' in entry)
      ) {
        return formatChangeValue(entry)
      }
      const gameId = String(entry.gameId)
      return t('activityLog.value.gameEntry', {
        game: names.byId.get(gameId) ?? gameId,
        value: String(entry.configuration),
      })
    })
    .toSorted((left, right) => left.localeCompare(right))
    .join(', ')
}

/**
 * One side of a cabinet's `configVersions` change (events from before
 * cabinets ran a configuration): each game's
 * assigned version by game name, with its configuration's name
 * (`Book of Ra 2: Low volatility · v2`; an event from before configurations
 * has only the number, `Book of Ra 2: v2`), sorted. "Default" for
 * none, a dash for nothing (a create's `from`), "…" while the names load; a
 * game without a name keeps its id.
 */
export function formatConfigVersions(
  value: unknown,
  names: GameNames,
  t: TFunction,
): string {
  if (!Array.isArray(value)) {
    return formatChangeValue(value)
  }
  if (value.length === 0) {
    return t('activityLog.value.default')
  }
  if (names.isLoading) {
    return '…'
  }
  return value
    .map((entry: unknown) => {
      if (
        typeof entry !== 'object' ||
        entry === null ||
        !('gameId' in entry) ||
        !('version' in entry)
      ) {
        return formatChangeValue(entry)
      }
      const gameId = String(entry.gameId)
      const version =
        'configuration' in entry && typeof entry.configuration === 'string'
          ? t('activityLog.value.versionWithConfiguration', {
              configuration: entry.configuration,
              version: String(entry.version),
            })
          : t('activityLog.value.version', { version: String(entry.version) })
      return t('activityLog.value.gameEntry', {
        game: names.byId.get(gameId) ?? gameId,
        value: version,
      })
    })
    .toSorted((left, right) => left.localeCompare(right))
    .join(', ')
}

export type ChangeRow = {
  field: string
  label: string
  from: string
  to: string
}

const NO_GAME_NAMES: GameNames = {
  byId: new Map(),
  isLoading: false,
  isError: false,
}

/**
 * One side of a cabinet's `language` or a tenant's or the platform's
 * `defaultLanguage` or `adminLanguage` change (a user's `language` too): the
 * language by its own name, "Default" for none
 * (Use default; no "(English)": what the default is may change, the log
 * keeps what was set), a code the contract no longer has as logged.
 */
function formatLanguageChange(value: unknown, t: TFunction): string {
  if (value === null || value === undefined) {
    return t('activityLog.value.default')
  }
  return isCabinetLanguage(value)
    ? LANGUAGE_NAMES[value]
    : formatChangeValue(value)
}

/**
 * One side of a user's `role` change: their platform role as Better Auth
 * stores it (`admin`, `technician`; `user` or nothing is none), by the names
 * the Users page shows. Any other value stays as logged.
 */
function formatPlatformRoleChange(value: unknown, t: TFunction): string {
  switch (value) {
    case 'admin':
      return t('activityLog.value.platformAdmin')
    case 'technician':
      return t('activityLog.value.technician')
    case 'user':
    case null:
    case undefined:
      return t('activityLog.value.none')
    default:
      return formatChangeValue(value)
  }
}

/** One side of a config version's `analyticRtp` (a fraction) as a percentage. */
function formatRtpChange(value: unknown): string {
  return typeof value === 'number' ? formatRtp(value) : formatChangeValue(value)
}

/**
 * One side of a cabinet's `credits` or `amount` change: stored units
 * (hundredths of a credit) as credits with two decimals; null (a credit-in's
 * `amount` has no "from") and anything that is no amount stay as logged.
 */
function formatCreditsChange(value: unknown): string {
  return typeof value === 'number' && Number.isSafeInteger(value)
    ? formatCredits(value)
    : formatChangeValue(value)
}

/**
 * The rows of the expanded "Field | From | To" table. A cabinet's games
 * (`gameIds`), its assigned configurations (`configurations`), a tenant's
 * default configurations (`defaultConfigurations`) and, in older
 * events, its assigned config versions (`configVersions`) show by game name
 * (`gameOptions`), its language and a tenant's or the platform's default
 * language by name, a config version's analytic RTP
 * as a percentage. A user's `role` is their platform role (a member's is a
 * tenant role, shown as logged).
 */
export function changeRows(
  changes: AuditChanges,
  t: TFunction,
  gameNames: GameNames = NO_GAME_NAMES,
  entity?: AuditEntity,
): ChangeRow[] {
  return Object.entries(changes).map(([field, { from, to }]) => {
    if (entity === 'user' && field === 'role') {
      return {
        field,
        label: fieldLabel('platformRole', t),
        from: formatPlatformRoleChange(from, t),
        to: formatPlatformRoleChange(to, t),
      }
    }
    if (entity === 'cabinet' && (field === 'credits' || field === 'amount')) {
      return {
        field,
        label: fieldLabel(field, t),
        from: formatCreditsChange(from),
        to: formatCreditsChange(to),
      }
    }
    switch (field) {
      case 'gameIds':
        return {
          field,
          label: fieldLabel(field, t),
          from: formatGameIds(from, gameNames, t),
          to: formatGameIds(to, gameNames, t),
        }
      case 'configurations':
      case 'defaultConfigurations':
        return {
          field,
          label: fieldLabel(field, t),
          from: formatConfigurations(from, gameNames, t),
          to: formatConfigurations(to, gameNames, t),
        }
      case 'configVersions':
        return {
          field,
          label: fieldLabel(field, t),
          from: formatConfigVersions(from, gameNames, t),
          to: formatConfigVersions(to, gameNames, t),
        }
      case 'language':
      case 'defaultLanguage':
      case 'adminLanguage':
        return {
          field,
          label: fieldLabel(field, t),
          from: formatLanguageChange(from, t),
          to: formatLanguageChange(to, t),
        }
      case 'analyticRtp':
        return {
          field,
          label: fieldLabel(field, t),
          from: formatRtpChange(from),
          to: formatRtpChange(to),
        }
      default:
        return {
          field,
          label: fieldLabel(field, t),
          from: formatChangeValue(from),
          to: formatChangeValue(to),
        }
    }
  })
}

/**
 * The `between` bounds of a picked day range: from the start of the first
 * day to the end of the last, so events of the last day are included.
 */
export function formatDateRange(
  range: DateRange | undefined,
): string[] | undefined {
  if (range?.from === undefined || range.to === undefined) {
    return undefined
  }
  return [
    startOfDay(range.from).toISOString(),
    endOfDay(range.to).toISOString(),
  ]
}
